import React from 'react';
import ZoneInput from './ZoneInput';
import MultiSelectSeparator from './MultiSelectSeparator';
import DraggableMultiSelectItem from './DraggableMultiSelectItem';
import DroppableMultiSelectSpace from './DroppableMultiSelectSpace';
import cloneDeep from 'lodash/cloneDeep';
import { KEY_TAB, KEY_ENTER } from '../../../constants/keyboardCodes';
import {
    ENTITY_PART_TYPE_KNOWN,
    ENTITY_PART_TYPE_SEPARATOR,
    DIRECTION_FORWARD,
    DIRECTION_BACKWARD,
    addPart,
    deleteItemAtPosition,
    normalizeParts,
    dragAndDropPart,
} from '../../../models/common/EntityPart';
import {
    createKnownZonePart,
    createSeparatorZonePart,
    ZONE_SEPARATOR_CHARACTERS,
} from '../../../models/ZonePart';
import { getDisplayZone } from 'common/types/GetDisplayZone';

class MultipleZoneSelect extends React.Component {
    constructor(props) {
        super(props);
        this.zoneSelected = this.zoneSelected.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.separatorEntered = this.separatorEntered.bind(this);
        this.zoneInput = React.createRef();
        this.handleOnTab = this.handleOnTab.bind(this);
        this.handleOnTabBack = this.handleOnTabBack.bind(this);
        this.handleOnEnter = this.handleOnEnter.bind(this);
        this.handleRemovedItem = this.handleRemovedItem.bind(this);
        this.moveCursorLeft = this.moveCursorLeft.bind(this);
        this.moveCursorRight = this.moveCursorRight.bind(this);
        this.onSetCursorPosition = this.onSetCursorPosition.bind(this);
        this.onDraggedItem = this.onDraggedItem.bind(this);

        this.defaultSeperatorCharacter =
            this.props.defaultSeperatorCharacter ||
            ZONE_SEPARATOR_CHARACTERS[0];

        const parts = this.initializeParts(props);
        this.state = {
            parts,
            initialValue: props.initialChar,
            cursorPos: parts.length,
        };
    }

    initializeParts(props) {
        let parts = [];
        const raw = props.value;
        const zones = Array.isArray(raw) ? raw : raw ? [raw] : [];
        for (const zoneName of zones) {
            const zone = {
                name: zoneName,
                displayName: getDisplayZone(zoneName),
            };
            const part = createKnownZonePart(zone);
            const result = addPart(
                parts,
                part,
                undefined,
                createSeparatorZonePart,
                this.defaultSeperatorCharacter
            );
            parts = result.parts;
        }
        return parts;
    }

    zoneSelected(zone, keyCode, shift) {
        this.setState(
            (prevState) => {
                const parts = cloneDeep(prevState.parts);
                const part = createKnownZonePart(zone);
                const { parts: newParts, cursorPos: newPosition } = addPart(
                    parts,
                    part,
                    prevState.cursorPos,
                    createSeparatorZonePart,
                    this.defaultSeperatorCharacter
                );
                return {
                    parts: newParts,
                    cursorPos: newPosition,
                    initialValue: null,
                };
            },
            () => {
                switch (keyCode) {
                    case KEY_TAB:
                        if (!shift) {
                            this.handleOnTab();
                        } else {
                            this.handleOnTabBack();
                        }
                        break;
                    case KEY_ENTER:
                        this.handleOnEnter();
                        break;
                    default:
                }
                this.focus();
            }
        );
    }

    separatorEntered(character) {
        this.setState((prevState) => {
            const parts = cloneDeep(prevState.parts);
            const part = createSeparatorZonePart(character);
            const newState = addPart(
                parts,
                part,
                prevState.cursorPos,
                createSeparatorZonePart,
                this.defaultSeperatorCharacter
            );
            return newState;
        }, this.focus);
    }

    handleOnTab() {
        if (this.props.onTab) {
            this.setState(
                (prevState) => this.doNormalizeParts(prevState),
                this.props.onTab
            );
        }
    }

    handleOnTabBack() {
        if (this.props.onTabBack) {
            this.setState(
                (prevState) => this.doNormalizeParts(prevState),
                this.props.onTabBack
            );
        }
    }

    handleOnEnter() {
        this.setState(
            (prevState) => this.doNormalizeParts(prevState),
            this.props.onEnter
        );
    }

    doNormalizeParts(prevState) {
        const parts = cloneDeep(prevState.parts);
        return {
            parts: normalizeParts(
                parts,
                createSeparatorZonePart,
                this.defaultSeperatorCharacter
            ),
        };
    }

    deleteAtPosition(parts, position, direction) {
        let nextPosition =
            direction === DIRECTION_BACKWARD ? position - 1 : position;
        ({ parts } = deleteItemAtPosition(parts, nextPosition, direction));
        nextPosition =
            direction === DIRECTION_BACKWARD
                ? Math.max(position - 1, 0)
                : position;
        return { parts, cursorPos: nextPosition };
    }

    moveCursorLeft() {
        const newPosition = this.state.cursorPos - 1;
        const nextPosition = newPosition >= 0 ? newPosition : 0;
        this.setState({ cursorPos: nextPosition }, this.focus.bind(this));
    }

    moveCursorRight() {
        const newPosition =
            this.state.cursorPos === 0 ? 1 : this.state.cursorPos + 1;
        const nextPosition =
            newPosition <= this.state.parts.length
                ? newPosition
                : this.state.parts.length;
        this.setState({ cursorPos: nextPosition }, this.focus.bind(this));
    }

    handleDelete(direction) {
        const parts = cloneDeep(this.state.parts);
        const position = this.state.cursorPos;
        const newState = this.deleteAtPosition(parts, position, direction);
        this.setState(
            { parts: newState.parts, cursorPos: newState.cursorPos },
            this.focus.bind(this)
        );
    }

    handleRemovedItem(index) {
        const parts = cloneDeep(this.state.parts);
        const newState = this.deleteAtPosition(parts, index, DIRECTION_FORWARD);
        this.setState(
            { parts: newState.parts, cursorPos: newState.cursorPos },
            this.focus.bind(this)
        );
    }

    get formattedValue() {
        return this.state.parts.map((part) => ({
            value: part.value,
            name: part.name,
            displayName: part.displayName,
            partType: part.partType,
        }));
    }

    focus() {
        if (this.zoneInput.current != null) {
            this.zoneInput.current.focus();
        }
    }

    onSetCursorPosition(index) {
        this.setState({ cursorPos: index }, this.focus.bind(this));
    }

    onDraggedItem(sourceIndex, targetIndex) {
        this.setState((prevState) => {
            const newParts = dragAndDropPart(
                prevState.parts,
                sourceIndex,
                targetIndex
            );
            const normalized = normalizeParts(
                newParts,
                createSeparatorZonePart,
                this.defaultSeperatorCharacter
            );
            return { parts: normalized, cursorPos: normalized.length };
        });
    }

    render() {
        const { className } = this.props;
        const isLastPosition = this.state.parts.length === this.state.cursorPos;

        const trailingSpace = (index) => (
            <DroppableMultiSelectSpace
                index={index}
                key="trailingSpace"
                onSetCursorPosition={this.onSetCursorPosition}
                onDraggedItem={this.onDraggedItem}
            />
        );

        const items = this.state.parts.map((part, index) => {
            const zoneName = part.displayName || part.value;
            const tooltip =
                part.displayName && part.displayName !== part.value
                    ? `${part.displayName} (${part.value})`
                    : undefined;
            switch (part.partType) {
                case ENTITY_PART_TYPE_KNOWN:
                    return (
                        <DraggableMultiSelectItem
                            key={index}
                            index={index}
                            value={zoneName}
                            tooltip={tooltip}
                            onRemovedItem={this.handleRemovedItem}
                            onSetCursorPosition={this.onSetCursorPosition}
                            onDraggedItem={this.onDraggedItem}
                            pill
                            tabIndex="-1"
                        />
                    );
                case ENTITY_PART_TYPE_SEPARATOR:
                    return (
                        <MultiSelectSeparator
                            key={index}
                            index={index}
                            value={zoneName}
                            onSetCursorPosition={this.onSetCursorPosition}
                            onDraggedItem={this.onDraggedItem}
                            onRemovedItem={this.handleRemovedItem}
                            pill
                            useHyphenSeparator={this.props.useHyphenSeparator}
                        />
                    );
                default:
                    throw new Error(`Unknown part type: ${part.partType}`);
            }
        });

        const cursor = (
            <div className="multipleLocationSelect-input-container" key="input">
                <div className="multipleLocationSelect-input">
                    <ZoneInput
                        ref={this.zoneInput}
                        context={this.props.context}
                        field={this.props.field}
                        headerName={this.props.headerName}
                        onZoneSelected={this.zoneSelected}
                        onSeparatorEntered={this.separatorEntered}
                        separatorCharacters={ZONE_SEPARATOR_CHARACTERS}
                        onDelete={this.handleDelete}
                        onChange={this.props.onChange}
                        inputClass={this.props.inputClass}
                        onTab={this.handleOnTab}
                        onTabBack={this.handleOnTabBack}
                        onEnter={this.handleOnEnter}
                        initialChar={this.state.initialValue}
                        cursorPos={this.state.cursorPos}
                        onMoveLeft={this.moveCursorLeft}
                        onMoveRight={this.moveCursorRight}
                        isLastPosition={isLastPosition}
                        shouldSelectItemOnTab={false}
                    />
                </div>
            </div>
        );

        items.splice(this.state.cursorPos, 0, cursor);
        if (this.state.cursorPos !== this.state.parts.length) {
            items.push(trailingSpace(this.state.parts.length));
        }

        return (
            <div className={`multiple-location-select ${className || ''}`}>
                <div className="multiple-location-select-items">{items}</div>
            </div>
        );
    }
}

export default MultipleZoneSelect;
